// Copyright (c) Docugami, Inc. All rights reserved.**/

import { Checkbox, ICheckboxStyles } from "@fluentui/react";
import { selectFilteredReportEntries as selectFilteredReportDocumentEntries } from "components/cards/ReportsWorkPane";
import { greyText, primaryTheme } from "components/shell/presentation/AppStyles";
import { ReportViewMode } from "hooks/navigationService";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { ReportEntry, ReportViewContext } from "networking/reportsApi";
import { useMemo } from "react";
import { AppState } from "store";
import { addFilter, removeFilter, ReportFilter, ReportFilterAxis, useGetFlaggedDocumentsQuery } from "store/reports/reportsSlice";
import { selectOrderedReportEntryIds, selectReportDocumentsView, selectReportId } from "store/reports/selectors";
import styled from "styled-components";
import { useDiligenceFlaggingEnabled } from "./WorkPathsHelper";
import { ITextFieldStyles, mergeStyleSets, TextField } from "@fluentui/react";
import { filterInputGrey } from "components/shell/presentation/AppStyles";
import { dgFontFamily } from "components/common/Fonts";
import { selectFilteredReportGridDocuments, selectFilteredReportGridEntries } from "./ReportDesignerCanvas";
import { skipToken } from "@reduxjs/toolkit/dist/query";

interface ReportFiltersState {
    isActive: boolean;
    filterTargetSearchName: string;
    countText: string;
}

export const useReportFiltersState = (): ReportFiltersState => {
    const searchString = useAppSelector(state => state.reportsSlice.searchString);
    const itemFilters = useAppSelector(state => state.reportsSlice.activeFilters);
    const filterAxis = useAppSelector(state => state.reportsSlice.filterAxis);
    const reportId = useAppSelector(selectReportId);
    const flagActive = itemFilters.includes(ReportFilter.Flagged);

    const documentsView = useAppSelector(selectReportDocumentsView);
    useGetFlaggedDocumentsQuery(!!reportId && flagActive ? { reportId } : skipToken);

    const filteredItems = useAppSelector(state => filterAxis === ReportFilterAxis.Documents ?
        getFilteredDocuments(state) : getFilteredEntries(state)
    );

    const totalItems = useAppSelector(state => filterAxis === ReportFilterAxis.Documents ? documentsView?.ids.length : selectOrderedReportEntryIds(state)?.length);

    const countText = totalItems === undefined || filteredItems === undefined ? '' : `Showing ${filteredItems.length} of ${totalItems} ${filterAxis === ReportFilterAxis.Documents ? "documents" : "items"}`;
    return {
        isActive: !!searchString || !!itemFilters.length,
        countText: countText,
        filterTargetSearchName: filterAxis === ReportFilterAxis.Documents ? "Document name" : "Item label"
    }
}

function getFilteredDocuments(state: AppState): string | string[] | undefined {
    return state.reportsSlice.viewMode === ReportViewMode.Table ? selectFilteredReportGridDocuments(state) : state.reportsSlice.folderId;
}

function getFilteredEntries(state: AppState): ReportEntry[] | undefined {
    return state.reportsSlice.viewMode === ReportViewMode.Table ?
        selectFilteredReportGridEntries(state, state.reportsSlice.reportId ?? '', state.reportsSlice.context) :
        selectFilteredReportDocumentEntries(state, state.reportsSlice.reportId ?? '', state.reportsSlice.context);
}

interface IFilterCheckbox {
    key: ReportFilter;
    label: string;
    checked?: boolean;
    onChange: (checked: boolean | undefined) => void;
    hidden?: boolean;
}

export const useCheckboxFilters = (): IFilterCheckbox[] => {
    const isPublished = useAppSelector(state => state.reportsSlice.context === ReportViewContext.Published);
    const activeFilters = useAppSelector(state => state.reportsSlice.activeFilters);
    const diligenceFlagsEnabled = useDiligenceFlaggingEnabled();

    const dispatch = useAppDispatch();

    return useMemo(() => {
        function onChange(filter: ReportFilter, checked: boolean) {
            if (checked) {
                dispatch(addFilter(filter));
            }
            else {
                dispatch(removeFilter(filter));
            }
        }

        let filters: IFilterCheckbox[] = [];

        if (!isPublished) {
            filters.push(
                {
                    key: ReportFilter.Unconfirmed,
                    label: "Unconfirmed",
                    checked: activeFilters.includes(ReportFilter.Unconfirmed),
                    onChange: (checked: boolean | undefined) => onChange(ReportFilter.Unconfirmed, !!checked)
                },
                {
                    key: ReportFilter.Confirmed,
                    label: "Fully Confirmed",
                    checked: activeFilters.includes(ReportFilter.Confirmed),
                    onChange: (checked: boolean | undefined) => onChange(ReportFilter.Confirmed, !!checked)
                }
            )
        }

        if (diligenceFlagsEnabled) {
            filters.push({
                key: ReportFilter.Flagged,
                label: "Flagged",
                checked: activeFilters.includes(ReportFilter.Flagged),
                onChange: (checked: boolean | undefined) => onChange(ReportFilter.Flagged, !!checked),
                hidden: false
            })
        }

        return filters;
    }, [activeFilters, diligenceFlagsEnabled, dispatch, isPublished])
}

const checkboxStyles: ICheckboxStyles = {
    text: {
        color: greyText,
        weight: 'bold',
        whiteSpace: 'nowrap'
    },
    checkbox: {
        marginTop: 2.5,
        width: 15,
        height: 15,
        borderRadius: 4
    }
}

export const FilterCheckbox = ({ filter }: { filter: IFilterCheckbox }): JSX.Element => {
    return (
        <Checkbox
            styles={checkboxStyles}
            label={filter.label}
            checked={filter.checked}
            onChange={(ev, checked) => filter.onChange(checked)} />
    )
}

export const FilteredCountText = styled.span`
    font-family: ${dgFontFamily};
    font-style: italic;
    font-size: 12px;
    text-transform: initial;
    color: ${primaryTheme.palette.themeDark};
`;

const textFieldStyles: Partial<ITextFieldStyles> = {
    root: {
        width: '100%',
        minWidth: "100px",
        'input::placeholder': {
            fontStyle: 'italic'
        }
    },
    fieldGroup: {
        background: filterInputGrey,
    }
}

const smallTextFieldStyles: Partial<ITextFieldStyles> = {
    root: {
        width: "200px"
    },
    fieldGroup: {
        height: 'fit-content'
    },
    field: {
        fontSize: '13px',
        lineHeight: '22px',
    }
}

interface ReportFilterSearchFieldProps {
    filterTargetLabel: string;
    filterText: string | undefined;
    onFilterTextChange: (value: string | undefined) => void;
    small?: boolean;
}

export const ReportFilterSearchField = (props: ReportFilterSearchFieldProps): JSX.Element => {
    return (
        <TextField
            styles={!!props.small ? mergeStyleSets(textFieldStyles, smallTextFieldStyles) : textFieldStyles}
            borderless
            placeholder={`Type ${props.filterTargetLabel.toLocaleLowerCase()} here...`}
            onChange={(event, newValue) => {
                props.onFilterTextChange(newValue);
            }}
            value={props.filterText} />
    )
}
